import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { invalidateForTask } from "@/lib/useTaskMutations";
import type { TaskRow } from "@shared/types";
import { Icon } from "./Icon";
import {
  ymd,
  monthMatrix,
  weekDays,
  WEEKDAY_LABELS,
  monthLabel,
} from "@/lib/calendar";
import { typeMeta } from "@/lib/workItemType";

type Users = ReadonlyMap<string, { name: string; image: string | null }>;

export function TaskCalendar({
  projectId,
  tasks,
  users,
}: {
  projectId: string;
  tasks: TaskRow[];
  users: Users;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"month" | "week">("month");
  const [cursor, setCursor] = useState<Date>(() => new Date());
  const [assignee, setAssignee] = useState<string>("");
  const [dragId, setDragId] = useState<string | null>(null);

  const patch = useMutation({
    mutationFn: ({ taskId, dueDate }: { taskId: string; dueDate: string }) =>
      api.patchTask(taskId, { dueDate }),
    onSuccess: () => invalidateForTask(qc, { projectId }),
  });

  const todayKey = ymd(new Date());

  const days = useMemo(
    () =>
      mode === "month"
        ? monthMatrix(cursor.getFullYear(), cursor.getMonth())
        : weekDays(cursor),
    [mode, cursor],
  );

  // Group filtered tasks by their due-date key.
  const byDay = useMemo(() => {
    const m = new Map<string, TaskRow[]>();
    for (const t of tasks) {
      if (!t.dueDate) continue;
      if (assignee) {
        const ids = t.assigneeIds?.length
          ? t.assigneeIds
          : t.assigneeId
            ? [t.assigneeId]
            : [];
        if (!ids.includes(assignee)) continue;
      }
      const key = t.dueDate.slice(0, 10);
      const arr = m.get(key) ?? [];
      arr.push(t);
      m.set(key, arr);
    }
    return m;
  }, [tasks, assignee]);

  function shift(delta: number) {
    const d = new Date(cursor);
    if (mode === "month") d.setMonth(d.getMonth() + delta);
    else d.setDate(d.getDate() + delta * 7);
    setCursor(d);
  }

  function onDropDay(dayKey: string) {
    if (dragId) {
      patch.mutate({ taskId: dragId, dueDate: dayKey });
      setDragId(null);
    }
  }

  return (
    <div className="cal-wrap">
      <div className="cal-bar">
        <div className="seg" role="group" aria-label="Calendar range">
          <button
            type="button"
            className={mode === "month" ? "seg-btn on" : "seg-btn"}
            aria-pressed={mode === "month"}
            onClick={() => setMode("month")}
          >
            Month
          </button>
          <button
            type="button"
            className={mode === "week" ? "seg-btn on" : "seg-btn"}
            aria-pressed={mode === "week"}
            onClick={() => setMode("week")}
          >
            Week
          </button>
        </div>
        <button
          type="button"
          className="chip-btn"
          onClick={() => shift(-1)}
          aria-label="Previous"
        >
          <Icon name="chev" size={14} />
        </button>
        <button
          type="button"
          className="chip-btn"
          onClick={() => setCursor(new Date())}
        >
          Today
        </button>
        <button
          type="button"
          className="chip-btn"
          onClick={() => shift(1)}
          aria-label="Next"
        >
          <Icon name="chev" size={14} />
        </button>
        <span className="cal-title">{monthLabel(cursor)}</span>
        <span style={{ flex: 1 }} />
        <select
          className="select"
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          aria-label="Filter by assignee"
          style={{ width: 170 }}
        >
          <option value="">All assignees</option>
          {[...users.entries()].map(([id, u]) => (
            <option key={id} value={id}>
              {u.name}
            </option>
          ))}
        </select>
      </div>

      <div className="cal-grid-head">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w} className="cal-wd">
            {w}
          </div>
        ))}
      </div>

      <div className={`cal-grid${mode === "week" ? " cal-week" : ""}`}>
        {days.map((d) => {
          const key = ymd(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = key === todayKey;
          const dayTasks = byDay.get(key) ?? [];
          return (
            <div
              key={key}
              className={`cal-cell${inMonth || mode === "week" ? "" : " cal-out"}${isToday ? " cal-today" : ""}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDropDay(key)}
            >
              <div className="cal-daynum">{d.getDate()}</div>
              <div className="cal-tasks">
                {dayTasks.map((t) => {
                  const overdue = key < todayKey && t.status !== "done";
                  return (
                    <button
                      key={t.id}
                      type="button"
                      draggable
                      onDragStart={() => setDragId(t.id)}
                      onDragEnd={() => setDragId(null)}
                      className={`cal-chip${overdue ? " cal-overdue" : ""}${t.status === "done" ? " cal-done" : ""}`}
                      style={{
                        borderLeftColor: typeMeta(t.type ?? "task").color,
                      }}
                      onClick={() =>
                        navigate({
                          to: "/tasks/$taskId",
                          params: { taskId: t.id },
                        })
                      }
                      title={t.title}
                    >
                      {t.title}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
